<template>
  <el-row :gutter="spacingWidthHeaderComponents">
    <el-col :span="5">
      <base-building-panel
        v-model:selectedBranch="selectedBranch"
        :branches="branches"
        :loading-branches="loadingBranches"
      >
        <template #item>
          <el-form-item :label="$t('category')">
            <el-select
              v-model="selectedCategory"
              :disabled="!selectedBranch.name || loadingCategories"
              value-key="name"
              size="small"
              :placeholder="loadingCategories ? $t('loading') : $t('empty')"
            >
              <el-option
                v-for="item in categories"
                :key="item.name ?? ''"
                :label="item.name ?? ''"
                :value="item"
              ></el-option>
            </el-select>
          </el-form-item>
        </template>
      </base-building-panel>
    </el-col>
    <el-col :span="19">
      <base-building-table
        v-model="nodes"
        :show-build-button="userCanStartEngagementPipelines"
        :loading-nodes="loadingNodes"
        @start-pipeline-run="startPipelineRun"
      />
    </el-col>
  </el-row>
</template>

<script lang="ts">
import { ElCol, ElRow, ElFormItem, ElSelect, ElOption } from 'element-plus';

import BaseBuildingPanel from '@/components/panels/BaseBuildingPanel.vue';
import BaseBuildingTable from '@/components/tables/BaseBuildingTable.vue';
import { computed, ComputedRef, onMounted } from 'vue';
import { spacingWidthHeaderComponents } from '@/utils/variables';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { appendUrlParam, processUrlParams } from '@/utils/urlParameter';
import {
  LayerName,
  StartPipelineRunDto
} from '@etp/etp-pipelines-client/axios';
import config from '@/config/config';
import { BranchModel, CategoryModel } from '@etp/etp-nodemodelgit-client/axios';

export default {
  name: 'ModelBuilding',
  components: {
    BaseBuildingPanel,
    BaseBuildingTable,
    ElCol,
    ElRow,
    ElFormItem,
    ElOption,
    ElSelect
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    let pipelineParameters = {
      EngagementId: store.getters['home/getCurrentEngagement'].id,
      TenantId: store.getters['home/getCurrentEngagement'].tenantId,
      Branch: store.getters['analytical/selectedBuildBranch'].name,
      Source: '',
      Category: store.getters['analytical/selectedBuildCategory'].name,
      ActiveNodes: store.getters['analytical/buildNodes']
        .filter(n => n.active)
        .map(n => n.name),
      RebuildNodes: store.getters['analytical/buildNodes']
        .filter(n => n.active && n.rebuild)
        .map(n => n.name)
    };

    const branches = computed(() => store.getters['analytical/branches']);
    const categories = computed<Array<CategoryModel>>(
      () => store.getters['analytical/buildCategories']
    );

    const nodes = computed({
      get: () => store.getters['analytical/buildNodes'],
      set: items => {
        store.commit('analytical/SET_BUILD_NODES', items);
        pipelineParameters.ActiveNodes = items
          .filter(n => n.active)
          .map(n => n.name);
        pipelineParameters.RebuildNodes = items
          .filter(n => n.active && n.rebuild)
          .map(n => n.name);
      }
    });

    let loadingBranches: ComputedRef<boolean> = computed(
      () => store.getters['analytical/loadingBranches']
    );

    let loadingCategories: ComputedRef<boolean> = computed(
      () => store.getters['analytical/loadingCategories']
    );

    let loadingNodes: ComputedRef<boolean> = computed(
      () => store.getters['analytical/loadingNodes']
    );

    const fetchBranches = async () => {
      await store.dispatch('analytical/fetchBranches');
    };
    const fetchCategories = async () => {
      await store.dispatch('analytical/fetchBuildCategories');
    };
    const fetchNodes = async () => {
      await store.dispatch('analytical/fetchBuildNodes');
    };
    const startPipelineRun = async () => {
      await store.dispatch('analytical/startPipelineRun', {
        currentLayer: LayerName.AnalyticalLayer,
        pipelineName: config.Pipeline.AnalyticalLayerPipelineName,
        parameters: pipelineParameters
      } as StartPipelineRunDto);
    };

    const selectedBranch = computed({
      get: () => store.getters['analytical/selectedBuildBranch'],
      set: async (branch: BranchModel) => {
        appendUrlParam('branch', branch.name ?? '');
        store.commit('analytical/SET_SELECTED_BUILD_BRANCH', branch);
        pipelineParameters.Branch = branch.name;
        await fetchCategories();
      }
    });
    const selectedCategory = computed({
      get: () => store.getters['analytical/selectedBuildCategory'],
      set: async (category: CategoryModel) => {
        appendUrlParam('branch', selectedBranch.value.name ?? '');
        appendUrlParam('category', category.name ?? '');
        store.commit('analytical/SET_SELECTED_BUILD_CATEGORY', category);
        pipelineParameters.Category = category.name;
        await fetchNodes();
      }
    });

    const showNodeModelGitPages: ComputedRef<boolean> = computed(
      () => store.getters['home/showNodeModelGitPages']
    );
    const userCanStartEngagementPipelines: ComputedRef<boolean> = computed(
      () => store.getters['userPermissions/canStartEngagementPipelines']
    );
    onMounted(async () => {
      if (!showNodeModelGitPages.value) {
        return router.push({ name: 'Overview' });
      }
      fetchBranches().then(async () => {
        await processUrlParams([
          {
            name: 'branch',
            handler: async (branchName: string) => {
              let branch = branches.value.find(
                (el: BranchModel) => el.name == branchName
              );
              pipelineParameters.Branch = branchName;
              if (!branch) return;
              store.commit('analytical/SET_SELECTED_BUILD_BRANCH', branch);
              await fetchCategories();
            }
          },
          {
            name: 'category',
            handler: async (categoryName: string) => {
              let category = categories.value.find(
                (el: CategoryModel) => el.name == categoryName
              );
              pipelineParameters.Category = categoryName;
              if (!category) return;
              store.commit('analytical/SET_SELECTED_BUILD_CATEGORY', category);
              await fetchNodes();
            }
          }
        ]);
      });
      await store.dispatch(
        'userPermissions/checkIfCanStartEngagementPipelines'
      );
    });

    return {
      branches,
      categories,
      startPipelineRun,
      loadingBranches,
      loadingCategories,
      loadingNodes,
      nodes,
      selectedBranch,
      selectedCategory,
      spacingWidthHeaderComponents,
      userCanStartEngagementPipelines
    };
  }
};
</script>
